import styled from "@emotion/styled";
import { PublicKey } from "@solana/web3.js";
import type { StepColumnsType } from "@stepfinance/react";
import {
  CheckboxFilter,
  formatBalance,
  getColumnSearchProps,
  imageFromPlatformName,
  makeSimpleFilters,
  StepAssetBreakdownPopoverChip,
  StepButtonDropdown,
  StepPopoverCellWrapper,
  StepTable,
  URLIcon,
} from "@stepfinance/react";
import type { PendingStakingReward, StakingPlatforms } from "@stepfinance/staking";
import type { FilterValue, TablePaginationConfig } from "antd/lib/table/interface";
import type { FC } from "react";
import { useEffect, useMemo, useState } from "react";

import { useEnrichedWallet } from "../../../contexts/enrichedWallet";
import { useLanguageContext } from "../../../contexts/language/language";
import { useConnectedWallet } from "../../../hooks";
import { useClaimAtlasLockerRewards } from "../../../hooks/mutations/staratlas/useClaimAtlasLockerRewards";
import { useClaimPolisLockerRewards } from "../../../hooks/mutations/staratlas/useClaimPolisLockerRewards";
import type { StakingActions, StakingPositions } from "../../../types/staking";
import { isAtlasLockPosition, isPolisLockPosition } from "../../../types/staking";
import { getTokenByMint } from "../../../utils/tokenInfo";
import { isPaginationHidden, useLocalStorageState } from "../../../utils/utils";
import { IconTextContainer, PAGE_SIZE_OPTIONS, StepPopoverAssetWithIconRow, TokenIconText } from "../../shared";
import CellValue from "../../Table/CellValue";
import { TokenIcon } from "../../TokenIcon";
import { ChevronCollapse } from "../ChevronCollapse/ChevronCollapse";
import { PendingText } from "./Staking";
import { TokenLockupPopover } from "./TokenLockupPopver";

interface SingleAssetStakingProps {
  stakingPositions: StakingPositions[];
}

export const SingleAssetStaking: FC<SingleAssetStakingProps> = ({ stakingPositions }) => {
  const wallet = useConnectedWallet();
  const { watching } = useEnrichedWallet();

  const [filteredInfo, setFilteredInfo] = useState<Record<string, FilterValue | null>>({});
  const [claimingId, setClaimingId] = useState("");
  const [pagination, setPagination] = useState<TablePaginationConfig>({});
  const [paginationSetting, setPaginationSetting] = useLocalStorageState(
    "singleAssetStakingPaginationSetting",
    PAGE_SIZE_OPTIONS.m[0],
  );

  const polisLockerMutation = useClaimPolisLockerRewards();
  const atlasLockerMutation = useClaimAtlasLockerRewards();

  const {
    currentLanguageData: {
      common,
      components: { table: tableText },
      pages: {
        dashboard: {
          modules: {
            staking: { singleAssetStaking: singleAssetStakingText },
          },
        },
      },
    },
  } = useLanguageContext();

  const totalValue = useMemo(
    () => stakingPositions.reduce((a, b) => a + (b.valueInUSD ?? 0) + (b.pendingRewardUSD ?? 0), 0),
    [stakingPositions],
  );

  const stakingActionsMap: { [Platform in StakingPlatforms]: StakingActions<StakingPositions> } = useMemo(() => {
    return {
      Raydium: {},
      Marinade: {},
      Bonfida: {},
      Apricot: {},
      Synthetify: {},
      "Star Atlas": {
        onClaim: (stake) => {
          if (wallet && stake.kind === "Star Atlas") {
            setClaimingId(stake.stakingMint.toBase58());
            if (isPolisLockPosition(stake)) {
              void polisLockerMutation.mutateAsync(stake).finally(() => setClaimingId(""));
            } else if (isAtlasLockPosition(stake)) {
              void atlasLockerMutation.mutateAsync(stake).finally(() => setClaimingId(""));
            }
          }
        },
      },
      Hubble: {},
      UXD: {},
      Hedge: {},
      Quarry: {},
    };
  }, [atlasLockerMutation, polisLockerMutation, wallet]);

  useEffect(() => {
    setPagination({
      hideOnSinglePage: isPaginationHidden(stakingPositions.length, PAGE_SIZE_OPTIONS.m),
      showSizeChanger: true,
      pageSizeOptions: PAGE_SIZE_OPTIONS.m,
      pageSize: parseInt(paginationSetting),
    });
  }, [stakingPositions.length, paginationSetting]);

  const pendingRewardsPopoverContent = ({ pendingReward, rewardMint, pendingRewardsList }) => {
    if (pendingRewardsList) {
      return pendingRewardsList.map((reward: PendingStakingReward, i) => {
        const tokenMint = reward.tokenMint instanceof PublicKey ? reward.tokenMint.toBase58() : reward.tokenMint;
        const tokenInfo = getTokenByMint(tokenMint);
        const decimals = tokenInfo?.decimals;
        const symbol = tokenInfo?.symbol || "";
        return (
          <StepPopoverAssetWithIconRow
            key={`staking-pending-reward-${i}`}
            tokenIconMintAddress={tokenMint}
            amount={formatBalance(reward.tokenAmount, decimals)}
            symbol={symbol}
          />
        );
      });
    } else {
      const tokenInfo = getTokenByMint(rewardMint);
      const decimals = tokenInfo?.decimals;
      const symbol = tokenInfo?.symbol || "";
      return (
        <StepPopoverAssetWithIconRow
          tokenIconMintAddress={rewardMint}
          amount={formatBalance(pendingReward, decimals)}
          symbol={symbol}
        />
      );
    }
  };

  const stakingTableColumns: StepColumnsType<StakingPositions> = [
    {
      title: common.platform,
      key: "kind",
      dataIndex: "kind",
      className: "column-string",
      filters: makeSimpleFilters(stakingPositions.map((position) => position.kind)),
      filterDropdown: (props) => <CheckboxFilter {...props} />,
      onFilter: (value, record) => record.kind.startsWith(value as StakingPlatforms),
      filteredValue: filteredInfo.kind ?? null,
      render(_, record) {
        return (
          <div style={{ display: "flex" }}>
            <URLIcon src={imageFromPlatformName(record.kind) ?? ""} />
            <TokenIconText bold>{record.kind}</TokenIconText>
          </div>
        );
      },
      sorter: (a, b) => a.kind.localeCompare(b.kind),
    },
    {
      title: common.asset,
      key: "stakingMint",
      dataIndex: "stakingMint",
      className: "column-string",
      render(_, { stakingMint }) {
        const tokenInfo = getTokenByMint(stakingMint.toBase58());
        return (
          <IconTextContainer>
            <TokenIcon mintAddress={stakingMint.toBase58()} />
            <TokenIconText>{tokenInfo?.symbol}</TokenIconText>
          </IconTextContainer>
        );
      },
      filteredValue: filteredInfo.stakingMint ?? null,
      ...getColumnSearchProps((record) => {
        const tokenInfo = getTokenByMint(record.stakingMint.toBase58());
        return tokenInfo ? tokenInfo.symbol : "";
      }, "asset"),
    },
    {
      title: "APY",
      key: "apy",
      dataIndex: "apy",
      className: "column-number",
      render(_, record) {
        return (
          <APYContainer>
            <StepPopoverCellWrapper>
              <div className="asset-value">
                <CellValue cellType="percentage">{record.apy}</CellValue>
              </div>
              <TokenLockupPopover record={record} common={common} singleAssetStakingText={singleAssetStakingText} />
            </StepPopoverCellWrapper>
          </APYContainer>
        );
      },
      sorter: (a, b) => (a.apy ?? 0) - (b.apy ?? 0),
    },
    {
      title: common.pendingRewards,
      key: "pendingReward",
      dataIndex: "pendingReward",
      className: "column-number",
      render(_, record) {
        const { kind, pendingReward, pendingRewardsList, pendingRewardUSD, rewardMint, stakingMint } = record;
        const isPendingReward = (!!pendingReward || !!pendingRewardsList) && !!pendingRewardUSD && pendingRewardUSD > 0;
        const showMultipleButton = !!stakingActionsMap[kind].onClaim && !watching && isPendingReward;
        return (
          <PendingRewardsContainer spaceBetween={showMultipleButton}>
            {isPendingReward ? (
              <>
                {showMultipleButton && (
                  <StepButtonDropdown
                    buttonTitle={common.claim}
                    onButtonClick={() => stakingActionsMap[kind].onClaim?.(record)}
                    items={[
                      {
                        label: common.claim,
                        hidden: pendingRewardUSD <= 0,
                        onClick: () => stakingActionsMap[kind].onClaim?.(record),
                        loading: claimingId === stakingMint.toBase58(),
                      },
                    ]}
                  />
                )}
                <PopoverAssetContainer>
                  <StepPopoverCellWrapper>
                    <StepAssetBreakdownPopoverChip
                      assetValue={pendingRewardUSD}
                      assetValueDisplay={<CellValue cellType="userPositionValue">{pendingRewardUSD}</CellValue>}
                      title={tableText.rewardBreakdown.title}
                      content={pendingRewardsPopoverContent({ pendingReward, rewardMint, pendingRewardsList })}
                    />
                  </StepPopoverCellWrapper>
                </PopoverAssetContainer>
              </>
            ) : (
              <PopoverAssetContainer>
                <CellValue cellType="text">
                  <PendingText>{common.pending}</PendingText>
                </CellValue>
              </PopoverAssetContainer>
            )}
          </PendingRewardsContainer>
        );
      },
      sorter: (a, b) => {
        if (!a.pendingReward || !b.pendingReward) {
          return 0;
        }
        return a.pendingReward - b.pendingReward;
      },
    },
    {
      title: tableText.valueOfYourPosition,
      key: "totalValueUSD",
      dataIndex: "totalValueUSD",
      className: "column-number",
      render(_, { amountStaked, valueInUSD, stakingMint }) {
        const tokenInfo = getTokenByMint(stakingMint.toBase58());
        const decimals = tokenInfo?.decimals;
        const symbol = tokenInfo?.symbol || "";
        const positionValueDisplay = formatBalance(amountStaked, decimals);

        return (
          <StepPopoverCellWrapper>
            <StepAssetBreakdownPopoverChip
              title={singleAssetStakingText.positionBreakdown}
              assetValueDisplay={<CellValue cellType="userPositionValue">{valueInUSD}</CellValue>}
              assetValue={valueInUSD ?? 0}
              content={
                <StepPopoverAssetWithIconRow
                  tokenIconMintAddress={stakingMint}
                  amount={positionValueDisplay}
                  symbol={symbol}
                />
              }
            />
          </StepPopoverCellWrapper>
        );
      },
      sorter: (a, b) => (a.valueInUSD ?? 0) - (b.valueInUSD ?? 0),
      defaultSortOrder: "descend",
    },
  ];

  return (
    <>
      {stakingPositions.length > 0 ? (
        <ChevronCollapse isCollapseChild title={singleAssetStakingText.title} value={totalValue}>
          <StepTable<StakingPositions>
            rowKey={({ kind, stakingMint, amountStaked, apy }) =>
              `${kind}-${amountStaked}-${stakingMint.toBase58()}-${Math.floor((apy ?? 0) * 1000)}`
            }
            columns={stakingTableColumns}
            dataSource={stakingPositions}
            pagination={pagination}
            onChange={(pagination, filters) => {
              setFilteredInfo(filters);
              setPaginationSetting(pagination.pageSize ? pagination.pageSize.toString() : PAGE_SIZE_OPTIONS.m[0]);
            }}
          />
        </ChevronCollapse>
      ) : null}
    </>
  );
};

const APYContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const PopoverAssetContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-left: 10px;
`;

const PendingRewardsContainer = styled.div<{ spaceBetween: boolean }>`
  display: flex;
  width: 100%;
  justify-content: ${(props) => (props.spaceBetween ? "space-between" : "flex-end")};
`;
